import { lowerCase, upperFirst } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";

import { BAND_PATHS } from "Bands/router";
import { EventBlocksType, IEvent, IIconBandSetting } from "Bands/types";
import { getEventBandId, getEventTypeIcon, getPeopleBlockStatus } from "Bands/utils";
import { Tooltip } from "shared/components";
import { FlexContainer, Icon, MutedDarkText } from "shared/styles";
import { colors } from "shared/styles/constants";
import { buildUrl } from "shared/utils";

import { EventService } from "Bands/services";
import { rgba } from "polished";
import { getStatus } from "shared/styles/defaults";
import { IFormOption } from "shared/types";
import { StyledBoldText, StyledCalendarItem, StyledCalendarItemText, StyledIcon } from "./styles";

interface CalendarItemProps {
  event: IEvent;
  showBand?: boolean;
  setSelectedEventBlocks?: (events: IFormOption[]) => void;
  setSelectedSourceEvent?: (events: IEvent | null) => void;
  blockType?: EventBlocksType;
  infoBlockType?: IIconBandSetting;
}

export const CalendarItem: FC<CalendarItemProps> = ({
  event,
  showBand,
  setSelectedEventBlocks,
  setSelectedSourceEvent,
  blockType,
  infoBlockType,
}) => {
  const [eventBlocks, setEventBlocks] = useState<{ label: string; value: any }[]>([]);

  const fetchData = async () => {
    const data = await EventService.getEventBlocksOfType(
      event.id,
      blockType as EventBlocksType,
      infoBlockType
    );

    setEventBlocks(data);
  };

  const [titleRef, seTitleRef] = useState<Element | null>();
  const [attendanceRef, setAttendanceRef] = useState<Element | null>();

  const typeIcon = useMemo(() => getEventTypeIcon(event.type), [event]);
  const bandId = useMemo(() => getEventBandId(event), [event]);

  const userAttendance = useMemo(
    () => (event.userAttendance ? getPeopleBlockStatus(event.userAttendance) : null),
    [event]
  );

  const userAttendaceColor = useMemo(
    () => getStatus(userAttendance?.iconStatus).iconColor,
    [userAttendance?.iconStatus]
  );

  useEffect(() => {
    if (blockType && !eventBlocks.length) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (blockType && !eventBlocks.length) return null;

  return (
    <>
      <StyledCalendarItem
        status={event.status}
        to={buildUrl(BAND_PATHS.BAND_EVENT, {
          bandId,
          eventId: event.id,
        })}
        onClick={e => {
          if (!blockType) return;

          e.preventDefault();

          setSelectedSourceEvent && setSelectedSourceEvent(event);
          setSelectedEventBlocks && setSelectedEventBlocks(eventBlocks);
        }}
        data-testid="calendar_event"
      >
        <StyledIcon>
          <Icon size="m" as={typeIcon} color={colors.grey4} />
        </StyledIcon>
        <StyledCalendarItemText>
          <FlexContainer>
            <StyledBoldText ref={seTitleRef}>{event.title}</StyledBoldText>
            {userAttendance && userAttendaceColor && (
              <span ref={setAttendanceRef} style={{ display: "flex" }}>
                <Icon
                  isCircle
                  size="l"
                  as={userAttendance?.icon}
                  backgroundColor={rgba(userAttendaceColor, 0.2)}
                  color={userAttendaceColor}
                />
              </span>
            )}
          </FlexContainer>
          {showBand && <MutedDarkText>{event.bandName}</MutedDarkText>}
        </StyledCalendarItemText>
      </StyledCalendarItem>
      <Tooltip
        content={upperFirst(lowerCase(event.status))}
        referenceElement={titleRef}
        placement="top"
      />
      <Tooltip
        content={userAttendance?.viewLabel}
        referenceElement={attendanceRef}
        placement="top"
      />
    </>
  );
};
